import { useState } from 'react';
import type { ConversationItem } from '@vivport/backend/types';
import { useNavigateWithContentFlush } from '@/hooks';
import { ChevronDown, ChevronRight, ExternalLink, Mail, Paperclip, PencilLine } from 'lucide-react';

type MessageReviewCardItem = Extract<ConversationItem, { kind: 'message_review_card' }>;

export interface ProvenanceMessageReviewCardProps {
  item: MessageReviewCardItem;
}

function fromLine(fromName: string | null, fromEmail: string | null): string | null {
  if (fromName && fromEmail) return `From ${fromName} <${fromEmail}>`;
  if (fromName) return `From ${fromName}`;
  if (fromEmail) return `From ${fromEmail}`;
  return null;
}

export function ProvenanceMessageReviewCard({ item }: ProvenanceMessageReviewCardProps) {
  const navigate = useNavigateWithContentFlush();
  const [expanded, setExpanded] = useState(false);

  const isDraftPreview = item.cardRole === 'DRAFT_PREVIEW';
  const from = fromLine(item.fromName, item.fromEmail);
  const receivedDate = item.receivedAt ? new Date(item.receivedAt).toLocaleDateString() : null;
  const metaParts = [from, receivedDate].filter((part): part is string => part !== null);
  const HeaderIcon = isDraftPreview ? PencilLine : Mail;

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="flex items-center gap-2">
        <HeaderIcon className="h-4 w-4 shrink-0" />
        {isDraftPreview ? <p className="shrink-0 font-medium">Draft reply</p> : null}
        <p className={`min-w-0 truncate ${isDraftPreview ? 'text-muted-foreground' : 'font-medium'}`}>
          {item.subject ?? '(no subject)'}
        </p>
        <button
          type="button"
          onClick={() => navigate(item.url)}
          className="ml-auto rounded p-1 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          aria-label="Open message"
          title="Open message"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>
      {isDraftPreview ? (
        <p className="mt-1 min-w-0 truncate text-xs text-muted-foreground">To {item.toSummary ?? '—'}</p>
      ) : metaParts.length > 0 || item.hasAttachments ? (
        <div className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <span className="min-w-0 truncate">{metaParts.join(' · ')}</span>
          {item.hasAttachments ? <Paperclip className="h-3 w-3 shrink-0" /> : null}
        </div>
      ) : null}
      {item.snippet ? (
        <p className="mt-1 truncate text-xs text-muted-foreground">{item.snippet}</p>
      ) : null}
      {item.spokenText !== null ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            className="inline-flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            )}
            <span>
              {`Read aloud · ${item.bodyVariant === 'FULL' ? 'full message' : 'main body'}${item.truncated ? ' · truncated' : ''}`}
            </span>
          </button>
          {expanded ? (
            <div className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">{item.spokenText}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
